"use client";

import type { TextColor } from "@homeinstead/schemas";

import { EventName } from "@homeinstead/shared";
import { submitFranchiseInquiry } from "~www/actions/submitFranchiseInquiry";
import {
  InquiryRedirectStatus,
  InquiryTwoStepForm,
  useInquiryLeadForm,
} from "~www/components/LeadForm/Inquiry";
import { FranchiseInquiryContactStep } from "./FranchiseInquiryContactStep";
import { FranchiseInquiryInfoStep } from "./FranchiseInquiryInfoStep";
import {
  franchiseInquiryFormSchema,
  getFranchiseInquiryDefaultFormValues,
} from "./franchiseInquiry.schema";
import { getFranchiseInquiryTrackingProperties } from "./get-franchise-inquiry-tracking-properties";

const FRANCHISE_INQUIRY_THANK_YOU_PATH = "/franchise/thank-you";

type FranchiseInquiryLeadFormProps = {
  blockKey: string;
  className?: string;
  pageDocumentId: string;
  textColor?: TextColor;
};

function FranchiseInquiryLeadForm({
  blockKey,
  className,
  pageDocumentId,
  textColor,
}: FranchiseInquiryLeadFormProps) {
  const {
    handleFormSubmit,
    isPending,
    isRedirecting,
    methods,
    setStep,
    step,
    submitError,
    submitFormAction,
  } = useInquiryLeadForm({
    eventName: EventName.FRANCHISE_INQUIRY_SUBMIT,
    getDefaultValues: getFranchiseInquiryDefaultFormValues,
    getTrackingProperties: getFranchiseInquiryTrackingProperties,
    schema: franchiseInquiryFormSchema,
    submitAction: submitFranchiseInquiry,
    thankYouPath: FRANCHISE_INQUIRY_THANK_YOU_PATH,
  });

  if (isRedirecting) {
    return (
      <div className={className}>
        <InquiryRedirectStatus textColor={textColor} />
      </div>
    );
  }

  return (
    <div className={className}>
      <InquiryTwoStepForm
        handleFormSubmit={handleFormSubmit}
        hiddenFields={
          <>
            <input name="inquiryBlockKey" type="hidden" value={blockKey} />
            <input
              name="inquiryDocumentId"
              type="hidden"
              value={pageDocumentId}
            />
          </>
        }
        methods={methods}
        onBack={() => setStep(1)}
        step={step}
        step1={
          <FranchiseInquiryInfoStep
            nextStep={() => setStep(2)}
            textColor={textColor}
          />
        }
        step2={
          <FranchiseInquiryContactStep
            isPending={isPending}
            submitError={submitError}
            textColor={textColor}
          />
        }
        submitFormAction={submitFormAction}
        textColor={textColor}
      />
    </div>
  );
}

export { FranchiseInquiryLeadForm };
